import { useMemo } from 'react';
import type { AccountDetailsActionButtonsVisibility } from './types';
import {
  useAccountsStore,
  useAssetTransferAvailability,
  type AccountType,
} from '@/entities/account';
import { useOpenAccountAvailabilityStore } from '@/features/account-open';
import { useSessionStore } from '@/entities/session';

const useAccountDetailsActionButtons = (type: AccountType) => {
  const { session } = useSessionStore();
  const isVerified = session?.additional?.isVerified || false;
  const { ewallet, spot, invest, leverage, earn, accountsQty } =
    useAccountsStore();
  const {
    openInvestIsAvailable,
    openMarginIsAvailable,
    openSpotIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();
  const { getAccountTypeTransferAvailability } = useAssetTransferAvailability();

  const actionButtonsVisibility: AccountDetailsActionButtonsVisibility | null =
    useMemo(() => {
      if (type === 'spot') {
        return {
          openNewAccount: openSpotIsAvailable && spotCreateIsActive,
          deposit: false,
          withdraw: false,
          exchange: false,
          transfer: getAccountTypeTransferAvailability(type),
          trade: true,
          data: true,
        };
      }

      if (type === 'invest') {
        return {
          openNewAccount: openInvestIsAvailable && investCreateIsActive,
          deposit: false,
          withdraw: false,
          exchange: false,
          transfer: getAccountTypeTransferAvailability(type),
          trade: true,
          data: false,
        };
      }

      if (type === 'earn') {
        return {
          openNewAccount: openEarnIsAvailable && earnCreateIsActive,
          deposit: false,
          withdraw: false,
          exchange: false,
          transfer: getAccountTypeTransferAvailability(type),
          trade: true,
          data: false,
        };
      }

      if (type === 'leverage') {
        return {
          openNewAccount: openMarginIsAvailable && marginCreateIsActive,
          deposit: false,
          withdraw: false,
          exchange: false,
          data: false,
          transfer: getAccountTypeTransferAvailability(type),
          trade: true,
        };
      }

      if (type === 'ewallet') {
        return {
          deposit: true,
          withdraw: true,
          exchange: true,
          transfer: getAccountTypeTransferAvailability(type),
          trade: false,
          data: isVerified,
          openNewAccount: false,
        };
      }

      return null;
    }, [
      isVerified,
      ewallet,
      spot,
      invest,
      leverage,
      earn,
      accountsQty,
      openInvestIsAvailable,
      openMarginIsAvailable,
      openSpotIsAvailable,
      openEarnIsAvailable,
      spotCreateIsActive,
      investCreateIsActive,
      marginCreateIsActive,
      earnCreateIsActive,
      getAccountTypeTransferAvailability,
    ]);

  return {
    actionButtonsVisibility,
  };
};

export { useAccountDetailsActionButtons };
